import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _isEqual from 'lodash/isEqual';
import _isObject from 'lodash/isPlainObject';

import { user, localStore, logError } from 'utils';
import {
  getMasterData,
  getAccessData,
  getConfiguration,
  getSubscription,
  logout as logoutUser
} from 'utils/services';
import {
  transformConfiguration,
  saveConfigurationInStorage,
  createPlanFeaturesMap,
  createFeaturesAccessMap,
  formatAndStoreMasterData
} from 'utils/methods';
import { useAuthGuard } from 'utils/hooks';
import { identifyUser } from 'utils/analytics';
import Loader from 'components/Loading/Loader';
import { STORAGE_KEYS } from 'utils/DICTIONARY';

const AuthContext = React.createContext();

class AuthProvider extends React.Component {
  state = {
    isLoggedIn: user.isLoggedIn(),
    ready: false,
    roles: {},
    accountInfo: {},
    masterData: {},
    featureAccess: {},
    userData: user.get(),
    isTermsAccepted: true,
    isSuperuser: false,
    isAddOnStore: false,
    isExternalUser: false,
    isTestRetailer: false,
    externalUserAccess: null,
    isTourShown: false,
    showTour: false,
    configuration: null,
    hasError: false,
    errorObject: null,
    accessToStores: [],
    changePasswordModal: false,
    accessBlocked: null,
    appLevelAlert: null,
    loadedAccessData: false,
    isWarehouseEnabled: false,
    isExchangeAllowed: true,
    storeProperties: {},
    subscriptionDetails: {},
    showSetupChecklist: false,
    isOnboardingComplete: true,
    showSubscriptionModal: false,
    isDefaultWarehouseSetup: false,
    passwordChangeRequired: user.isPasswordChangeRequired(),
    appType: {
      retex: process.env.REACT_APP_TYPE !== 'SHIPMENT_TRACKING',
      shipmentTracking: process.env.REACT_APP_TYPE === 'SHIPMENT_TRACKING'
    }
  };
  planFeatures = [];
  planFeaturesMap = {};
  featuresPlanMap = {};
  uniqueCategoryPlanFeaturesMap = {};
  subscriptionCheckInterval = null;

  componentDidMount() {
    const { authCache } = this.props;

    getMasterData(
      masterData => this.setState({ masterData }),
      () => {},
      () => this.setState({ ready: true })
    );

    if (authCache) {
      this.login(authCache);
    }

    // Only perform protected API calls withing this block 👇
    if (this.state.passwordChangeRequired) {
      this.setState({ loadedAccessData: true });
    } else {
      this.loadAccessData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.hasError && prevProps.location !== this.props.location) {
      this.setState({ hasError: false, errorObject: null });
    }

    if (
      this.state.isLoggedIn &&
      !user.isLoggedIn() &&
      this.props.location.pathname !== '/logout'
    ) {
      return logoutUser();
    }

    const {
      ready,
      appType,
      loadedAccessData,
      subscriptionDetails
    } = this.state;
    const source = _get(subscriptionDetails, 'source', '');
    const canRefetch = _get(subscriptionDetails, 'show_usage_metrics', false);
    const usedCount = _get(subscriptionDetails, 'requests_processed_count', 0);
    const allowedCount = _get(subscriptionDetails, 'allowed_requests_count', 0);
    const isQuotaExhausted = canRefetch && usedCount >= allowedCount;

    if (
      canRefetch &&
      appType.retex &&
      loadedAccessData &&
      !prevState.loadedAccessData
    ) {
      // setup time interval to refetch subscription data
      this.subscriptionCheckInterval = setInterval(() => {
        this.syncSubscription();
      }, 3_600_000); // every hour

      // setup daily lookup to remind for subscription in case quota exhausted
      if (source === 'SHOPIFY' && isQuotaExhausted) {
        const localStoreKey = 'rr__last-quota-exhaust-reminder-date';
        const dateFormat = 'DD-MM-YYYY';
        const lastShownDate = JSON.parse(localStore.get(localStoreKey));
        const today = moment().format(dateFormat);
        if (lastShownDate !== today) {
          this.setState({ showSubscriptionModal: true });
          localStore.put(localStoreKey, JSON.stringify(today));
        }
      }
    }

    if (ready && loadedAccessData && !prevState.loadedAccessData) {
      /** @type {import("utils/types").EcommPlatformSource} */
      const ecommPlatform = _get(
        this.state.storeProperties,
        'ecommerce_platform.source'
      );

      if (ecommPlatform) {
        this.setState(currState => {
          const { masterData } = formatAndStoreMasterData(
            currState.masterData,
            ecommPlatform === 'BIGCOMMERCE'
          );
          return { masterData };
        });
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.subscriptionCheckInterval);
  }

  updateState = (updates = {}) => {
    if (!_isObject(updates)) return;
    this.setState(updates);
  };

  toggleChangePasswordModal = () => {
    if (this.state.isExternalUser) return;
    this.setState({ changePasswordModal: !this.state.changePasswordModal });
  };

  setAppLevelAlert = (type, message) => {
    this.setState({
      appLevelAlert: {
        context: type,
        message: message
      }
    });
  };

  clearAppLevelAlert = () => this.setState({ appLevelAlert: null });

  loadAccessData = (callback = () => {}) => {
    const { isLoggedIn, passwordChangeRequired, appType } = this.state;
    if (!isLoggedIn) return;

    if (passwordChangeRequired) {
      return this.setState({ loadedAccessData: true });
    }

    getAccessData((error, data) => {
      if (!error) {
        const planFeatures = _get(data, 'planFeatures', []);
        const configuration = _get(data, 'configuration', {});
        const subscriptionDetails = _get(data, 'subscription', {});
        const features = _get(subscriptionDetails, 'features', []);
        const configData = transformConfiguration(configuration, appType);
        const isSyncComplete = _get(configData.syncStatus, 'completed', false);

        const {
          planFeaturesMap,
          featuresPlanMap,
          uniqueCategoryPlanFeaturesMap
        } = createPlanFeaturesMap(planFeatures);
        saveConfigurationInStorage(configuration);
        this.planFeatures = planFeatures;
        this.planFeaturesMap = planFeaturesMap;
        this.featuresPlanMap = featuresPlanMap;
        this.uniqueCategoryPlanFeaturesMap = uniqueCategoryPlanFeaturesMap;
        this.setState(
          {
            ...configData,
            subscriptionDetails,
            loadedAccessData: true,
            featureAccess: createFeaturesAccessMap(features)
          },
          () => callback(true)
        );
        if (!isSyncComplete) {
          this.syncConfiguration(callback);
        }
      } else {
        this.error(new Error('Could not load configuration.'));
        this.setState({ loadedAccessData: true });
        callback(false);
      }
    });
  };

  syncConfiguration = (callback = () => {}, delay = 5_000) => {
    const { isLoggedIn, passwordChangeRequired, appType } = this.state;

    if (!isLoggedIn || passwordChangeRequired) return;

    setTimeout(() => {
      getConfiguration(
        configuration => {
          const configData = transformConfiguration(configuration, appType);
          const hasSynced = _get(configData.syncStatus, 'completed', false);
          this.setState({ ...configData });
          if (!hasSynced) this.syncConfiguration(callback);
          callback(true);
        },
        () => callback(false)
      );
    }, delay);
  };

  syncSubscription = (forceRefresh = false) => {
    const { subscriptionDetails, appType } = this.state;
    const canRefetch = _get(subscriptionDetails, 'show_usage_metrics', false);
    if (appType.retex && (canRefetch || forceRefresh)) {
      getSubscription(data => {
        const isSame = _isEqual(data, subscriptionDetails);
        const features = _get(data, 'features', []);
        if (isSame) return;
        this.setState({
          subscriptionDetails: data,
          featureAccess: createFeaturesAccessMap(features)
        });
      });
    }
  };

  updateSubscriptionDetails = (isSubscribed = false) => {
    this.setState(currState => {
      const currSubscription = currState.subscriptionDetails;
      const subscribed = _get(currSubscription, 'is_subscribed', false);

      if (subscribed === isSubscribed) return currState;

      return {
        subscriptionDetails: {
          ...currState.subscriptionDetails,
          is_subscribed: isSubscribed
        }
      };
    });
  };

  logout = (onLogout = () => {}) => {
    logoutUser(onLogout);
    clearInterval(this.subscriptionCheckInterval);
    this.planFeatures = [];
    this.planFeaturesMap = {};
    this.featuresPlanMap = {};
    this.uniqueCategoryPlanFeaturesMap = {};
    this.setState({
      isLoggedIn: false,
      userData: null,
      storeProperties: {},
      configuration: null,
      subscriptionDetails: {},
      loadedAccessData: false,
      passwordChangeRequired: false
    });
  };

  login = data => {
    try {
      const { appType } = this.state;
      const subscriptionDetails = _get(data, 'subscription', {});
      const features = _get(subscriptionDetails, 'features', []);
      const planFeatures = _get(data, 'billing_plans', {});
      let userData = _get(data, 'userData', {});
      const configuration = _get(userData, 'configuration', {});
      userData = _omit(userData, 'configuration');
      const storageUserData = JSON.stringify(userData);
      userData = _omit(userData, 'groups');

      const configData = transformConfiguration(configuration, appType);
      const { storeProperties, syncStatus } = configData;

      const storeUniqueName = _get(storeProperties, 'store_unique_name', '');

      const sellerStoreGuId = _get(storeProperties, 'store_unique_guid', '');
      const rrStoreId = _get(storeProperties, 'retailer_id', '');
      const needPasswordChange = _get(data, 'password_change.is_needed', false);
      const isSyncComplete = _get(syncStatus, 'completed', false);

      localStore.put(STORAGE_KEYS.LOGGED_IN_STORE_NAME, storeUniqueName);
      localStore.put(STORAGE_KEYS.LOGGED_IN_STORE_GUID, sellerStoreGuId);
      localStore.put(STORAGE_KEYS.LOGGED_IN_STORE_ID, rrStoreId);
      localStore.put(STORAGE_KEYS.USER_DATA, storageUserData);
      localStore.put(
        STORAGE_KEYS.PASSWORD_CHANGE_REQUIRED,
        JSON.stringify(needPasswordChange)
      );
      saveConfigurationInStorage(configuration);
      const {
        planFeaturesMap,
        featuresPlanMap,
        uniqueCategoryPlanFeaturesMap
      } = createPlanFeaturesMap(planFeatures);
      this.planFeatures = planFeatures;
      this.planFeaturesMap = planFeaturesMap;
      this.featuresPlanMap = featuresPlanMap;
      this.uniqueCategoryPlanFeaturesMap = uniqueCategoryPlanFeaturesMap;
      identifyUser();
      this.setState({
        userData,
        isLoggedIn: true,
        loadedAccessData: true,
        subscriptionDetails,
        passwordChangeRequired: needPasswordChange,
        featureAccess: createFeaturesAccessMap(features),
        ...configData
      });
      if (!isSyncComplete) {
        this.syncConfiguration();
      }
    } catch (e) {}
  };

  changeLocalStoreData = (storageKey, storageValue) => {
    if (!storageValue && !storageKey) return;
    localStore.put(storageKey, storageValue);
  };

  error = errorObject => {
    if (this.state.hasError) return;
    if (errorObject) {
      const error =
        errorObject instanceof Error
          ? errorObject
          : new Error(
              _get(errorObject, 'message') ||
                _get(errorObject, 'displayMessage') ||
                'Unknown error'
            );
      logError(error, { originalErrorObject: errorObject });
    }
    this.setState({ hasError: true, errorObject });
  };

  isDummyMerchant(state) {
    const name = _get(state, 'storeProperties.store_name') || '';
    return name.toLowerCase().indexOf('zenwear') > -1;
  }

  defaultWareHouseSetupDone = () => {
    this.setState({ isDefaultWarehouseSetup: true });
  };

  onToggleSetupChecklist = () => {
    this.setState(currState => ({
      showSetupChecklist: !currState.showSetupChecklist
    }));
  };

  render() {
    let {
      ready,
      roles,
      appType,
      hasError,
      userData,
      masterData,
      isLoggedIn,
      syncStatus,
      accountInfo,
      errorObject,
      isSuperuser,
      isAddOnStore,
      isExternalUser,
      isTestRetailer,
      externalUserAccess,
      isTourShown,
      showTour,
      accessBlocked,
      configuration,
      featureAccess,
      appLevelAlert,
      isTermsAccepted,
      storeProperties,
      accessToStores,
      isExchangeAllowed,
      isWarehouseEnabled,
      showSetupChecklist,
      changePasswordModal,
      loadedAccessData,
      subscriptionDetails,
      isOnboardingComplete,
      showSubscriptionModal,
      passwordChangeRequired,
      isDefaultWarehouseSetup
    } = this.state;

    if (!ready || (isLoggedIn && !loadedAccessData)) {
      return <Loader />;
    }

    if (accessBlocked && accessBlocked.message) {
      appLevelAlert = {
        allowClose: false,
        context: 'warn',
        message: accessBlocked.message
      };
    }

    return (
      <AuthContext.Provider
        value={{
          roles,
          appType,
          hasError,
          masterData,
          isLoggedIn,
          syncStatus,
          accountInfo,
          errorObject,
          isSuperuser,
          isAddOnStore,
          isExternalUser,
          isTestRetailer,
          externalUserAccess,
          showTour,
          isTourShown,
          configuration,
          featureAccess,
          appLevelAlert,
          accessBlocked,
          isTermsAccepted,
          storeProperties,
          accessToStores,
          isExchangeAllowed,
          isWarehouseEnabled,
          showSetupChecklist,
          changePasswordModal,
          subscriptionDetails,
          isOnboardingComplete,
          showSubscriptionModal,
          passwordChangeRequired,
          isDefaultWarehouseSetup,
          planFeatures: this.planFeatures,
          planFeaturesMap: this.planFeaturesMap,
          featuresPlanMap: this.featuresPlanMap,
          uniqueCategoryPlanFeaturesMap: this.uniqueCategoryPlanFeaturesMap,
          userData: isLoggedIn ? userData : null,
          login: this.login,
          logout: this.logout,
          generateAppError: this.error,
          updateState: this.updateState,
          syncConfiguration: this.syncConfiguration,
          syncSubscription: this.syncSubscription,
          toggleChangePasswordModal: this.toggleChangePasswordModal,
          updateSubscriptionDetails: this.updateSubscriptionDetails,
          dummyMerchant: this.isDummyMerchant(this.state),
          setAppLevelAlert: this.setAppLevelAlert,
          clearAppLevelAlert: this.clearAppLevelAlert,
          onToggleSetupChecklist: this.onToggleSetupChecklist,
          changeLocalStoreData: this.changeLocalStoreData,
          defaultWareHouseSetupDone: this.defaultWareHouseSetupDone
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}

const Consumer = AuthContext.Consumer;

const Provider = withRouter(props => {
  const { authCache } = useAuthGuard();
  return <AuthProvider {...props} authCache={authCache} />;
});

export { AuthContext, Provider as AuthProvider, Consumer as AuthConsumer };
